/**
 * ExtractionReviewDashboard — Main review interface for source coordinate maps
 *
 * Pattern: mirrors ValidationReviewDashboard — Tabs + Accordion + Badges
 *
 * Tabs:
 *   1. "Pending" — unverified extractions
 *   2. "Verified" — confirmed/corrected extractions
 *   3. "Rejected" — rejected with correction notes
 *   4. "Summary" — coverage by layer, confidence distribution
 *
 * CRITICAL: Zero domain-specific props. value_type drives rendering.
 * New source maps auto-discovered when config is seeded to DynamoDB.
 */

import React, { useMemo } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Alert,
  Badge,
  Tabs,
  LoadingOverlay,
  Progress,
} from '@mantine/core';
import {
  IconClipboardCheck,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconFileSearch,
} from '@tabler/icons-react';
import { ExtractionCard, type Extraction } from './ExtractionCard';

interface SourceDocument {
  documentId: string;
  filename: string;
  totalPages: number | null;
}

interface ExtractorInfo {
  provider: string;
  model: string;
}

interface ExtractionSummary {
  totalExtractions: number;
  verifiedCount: number;
  rejectedCount: number;
  unverifiedCount: number;
  averageConfidence: number;
}

export interface SourceCoordinateMap {
  mappingId: string;
  sourceDocument: SourceDocument;
  ingestedAt: string;
  extractor: ExtractorInfo;
  reviewStatus: string;
  extractions: Extraction[];
  summary: ExtractionSummary;
}

interface ExtractionReviewDashboardProps {
  /** The source map to review */
  sourceMap: SourceCoordinateMap | null;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Callback when user verifies an extraction */
  onVerifyExtraction: (extractionId: string, status: 'CONFIRMED' | 'CORRECTED', note?: string) => void;
  /** Callback when user rejects an extraction */
  onRejectExtraction: (extractionId: string, reason: string) => void;
  /** Callback for bulk verify */
  onBulkVerify?: (confidenceThreshold: number) => void;
  /** Callback when all extractions are reviewed */
  onComplete?: () => void;
}

export function ExtractionReviewDashboard({
  sourceMap,
  loading = false,
  error = null,
  onVerifyExtraction,
  onRejectExtraction,
  onBulkVerify,
}: ExtractionReviewDashboardProps) {
  const pending = useMemo(
    () => sourceMap?.extractions.filter((e) => e.verificationStatus === 'UNVERIFIED') ?? [],
    [sourceMap],
  );

  const verified = useMemo(
    () =>
      sourceMap?.extractions.filter(
        (e) => e.verificationStatus === 'CONFIRMED' || e.verificationStatus === 'CORRECTED',
      ) ?? [],
    [sourceMap],
  );

  const rejected = useMemo(
    () => sourceMap?.extractions.filter((e) => e.verificationStatus === 'REJECTED') ?? [],
    [sourceMap],
  );

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error}
      </Alert>
    );
  }

  if (!sourceMap && !loading) {
    return (
      <Alert icon={<IconFileSearch size={16} />} title="Not Found" color="gray">
        Source map not found or not yet loaded.
      </Alert>
    );
  }

  const total = sourceMap?.summary.totalExtractions ?? 0;
  const reviewProgress = total > 0 ? ((verified.length + rejected.length) / total) * 100 : 0;

  return (
    <Container size="lg" pos="relative">
      <LoadingOverlay visible={loading} />

      <Stack gap="md">
        {/* Header */}
        <Paper p="md" withBorder>
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Title order={3}>
                <IconClipboardCheck size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Extraction Review
              </Title>
              {sourceMap && (
                <Text size="sm" c="dimmed">
                  {sourceMap.sourceDocument.filename}
                  {sourceMap.sourceDocument.totalPages &&
                    ` (${sourceMap.sourceDocument.totalPages} pages)`}
                  {' — '}Extracted by {sourceMap.extractor.model}
                </Text>
              )}
            </Stack>
            <Group gap="xs">
              <Badge color="gray">{total} total</Badge>
              <Badge color="blue">{pending.length} pending</Badge>
              <Badge color="green">{verified.length} verified</Badge>
              <Badge color="red">{rejected.length} rejected</Badge>
            </Group>
          </Group>

          {/* Progress bar */}
          <Progress
            value={reviewProgress}
            mt="sm"
            size="sm"
            color={reviewProgress === 100 ? 'green' : 'blue'}
          />
          <Text size="xs" c="dimmed" ta="right" mt={4}>
            {Math.round(reviewProgress)}% reviewed
          </Text>
        </Paper>

        {/* Tabs */}
        <Paper p="md" withBorder>
          <Tabs defaultValue="pending">
            <Tabs.List>
              <Tabs.Tab value="pending" leftSection={<IconFileSearch size={14} />}>
                Pending ({pending.length})
              </Tabs.Tab>
              <Tabs.Tab value="verified" leftSection={<IconCheck size={14} />}>
                Verified ({verified.length})
              </Tabs.Tab>
              <Tabs.Tab value="rejected" leftSection={<IconX size={14} />}>
                Rejected ({rejected.length})
              </Tabs.Tab>
              <Tabs.Tab value="summary">Summary</Tabs.Tab>
            </Tabs.List>

            {/* Pending tab */}
            <Tabs.Panel value="pending" pt="md">
              <Stack gap="sm">
                {onBulkVerify && pending.length > 0 && (
                  <Group justify="flex-end">
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      onClick={() => onBulkVerify(0.9)}
                    >
                      Verify All High Confidence (&gt;90%)
                    </Button>
                  </Group>
                )}
                {pending.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">
                    No pending extractions. All have been reviewed.
                  </Text>
                ) : (
                  pending.map((extraction) => (
                    <ExtractionCard
                      key={extraction.id}
                      extraction={extraction}
                      onVerify={onVerifyExtraction}
                      onReject={onRejectExtraction}
                    />
                  ))
                )}
              </Stack>
            </Tabs.Panel>

            {/* Verified tab */}
            <Tabs.Panel value="verified" pt="md">
              <Stack gap="sm">
                {verified.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">
                    No verified extractions yet.
                  </Text>
                ) : (
                  verified.map((extraction) => (
                    <ExtractionCard
                      key={extraction.id}
                      extraction={extraction}
                      onVerify={onVerifyExtraction}
                      onReject={onRejectExtraction}
                      disabled
                    />
                  ))
                )}
              </Stack>
            </Tabs.Panel>

            {/* Rejected tab */}
            <Tabs.Panel value="rejected" pt="md">
              <Stack gap="sm">
                {rejected.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">
                    No rejected extractions.
                  </Text>
                ) : (
                  rejected.map((extraction) => (
                    <ExtractionCard
                      key={extraction.id}
                      extraction={extraction}
                      onVerify={onVerifyExtraction}
                      onReject={onRejectExtraction}
                      disabled
                    />
                  ))
                )}
              </Stack>
            </Tabs.Panel>

            {/* Summary tab */}
            <Tabs.Panel value="summary" pt="md">
              {sourceMap && (
                <Stack gap="md">
                  <Group gap="lg">
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed">
                        Average Confidence
                      </Text>
                      <Text size="xl" fw={700}>
                        {Math.round(sourceMap.summary.averageConfidence * 100)}%
                      </Text>
                    </Stack>
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed">
                        Total Extractions
                      </Text>
                      <Text size="xl" fw={700}>
                        {sourceMap.summary.totalExtractions}
                      </Text>
                    </Stack>
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed">
                        Review Status
                      </Text>
                      <Badge size="lg" color={sourceMap.reviewStatus === 'VERIFIED' ? 'green' : 'blue'}>
                        {sourceMap.reviewStatus}
                      </Badge>
                    </Stack>
                  </Group>

                  {/* Layer distribution */}
                  <Stack gap={4}>
                    <Text size="sm" fw={600}>
                      Extractions by Layer
                    </Text>
                    {(() => {
                      const layers = new Map<string, number>();
                      for (const ext of sourceMap.extractions) {
                        const layer = ext.target.layer;
                        layers.set(layer, (layers.get(layer) ?? 0) + 1);
                      }
                      return Array.from(layers.entries()).map(([layer, count]) => (
                        <Group key={layer} justify="space-between">
                          <Text size="sm">{layer}</Text>
                          <Badge variant="light">{count}</Badge>
                        </Group>
                      ));
                    })()}
                  </Stack>
                </Stack>
              )}
            </Tabs.Panel>
          </Tabs>
        </Paper>
      </Stack>
    </Container>
  );
}
